// Fetch a JSON file from /public and share it between every component that
// asks — hopping between pages never refetches. Data files are kept live:
// when the tab comes back into view (or every few minutes while it's open)
// they're revalidated, and components re-render only if the file changed.
// Returns null while loading, false on failure, the parsed JSON otherwise.
import { useEffect, useState } from "react";

const STALE_MS = 60 * 1000;
const POLL_MS = 5 * 60 * 1000;

const entries = {}; // path -> { promise, text, data, at, subs:Set }

const entry = (path) => (entries[path] = entries[path] || { subs: new Set(), at: 0 });

const fetchJson = (path) => {
  const e = entry(path);
  e.at = Date.now();
  e.promise = fetch(`${process.env.PUBLIC_URL}/${path}`, { cache: "no-cache" })
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error(r.status))))
    .then((text) => {
      if (text !== e.text) {
        e.text = text;
        e.data = JSON.parse(text);
        e.subs.forEach((fn) => fn(e.data));
      }
      return e.data;
    })
    .catch(() => {
      e.at = 0; // retry next time someone asks
      return e.data === undefined ? false : e.data;
    });
  return e.promise;
};

export const loadJson = (path) => {
  const e = entry(path);
  return e.promise && e.at ? e.promise : fetchJson(path);
};

// revalidate everything that's on screen
const refresh = (force) => {
  Object.entries(entries).forEach(([path, e]) => {
    if (e.subs.size && (force || Date.now() - e.at > STALE_MS)) fetchJson(path);
  });
};

if (typeof window !== "undefined") {
  const onVisible = () => document.visibilityState === "visible" && refresh();
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("focus", onVisible);
  window.addEventListener("online", () => refresh(true));
  setInterval(() => document.visibilityState === "visible" && refresh(true), POLL_MS);
}

const useJson = (path) => {
  const [data, setData] = useState(() => (path && entries[path] && entries[path].data) || null);
  useEffect(() => {
    if (!path) return undefined;
    let alive = true;
    const e = entry(path);
    const sub = (d) => alive && setData(d);
    e.subs.add(sub);
    if (e.data !== undefined) setData(e.data);
    else setData(null);
    loadJson(path).then((d) => alive && setData(d));
    return () => {
      alive = false;
      e.subs.delete(sub);
    };
  }, [path]);
  return data;
};

export const useFitness = () => useJson("fitness.json");
export const useTennis = () => useJson("tennis.json");
export const useGeomap = () => useJson("geomap.json");
export const useJournal = () => useJson("journal.json");
export const useActivity = (id) => useJson(id ? `activities/${id}.json` : null);

export default useJson;
